import React, { useState, useEffect } from 'react';
import { X, Users, AlertCircle, Plus, Edit, Power, Trash2, Building2, Save, ShieldCheck } from 'lucide-react';
import { HospitalGroup, StaffUser, StaffRole } from '../types';
import * as storageService from '../services/storageService';

interface GroupAccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    group: HospitalGroup | null;
}

const GroupAccessModal: React.FC<GroupAccessModalProps> = ({ isOpen, onClose, group }) => {
    // -- STAFF STATE --
    const [staffList, setStaffList] = useState<StaffUser[]>([]);
    const [isLoadingStaff, setIsLoadingStaff] = useState(false);
    const [editingStaff, setEditingStaff] = useState<StaffUser | null>(null);
    const [isStaffFormOpen, setIsStaffFormOpen] = useState(false);
    const [staffForm, setStaffForm] = useState({ name: '', email: '' });
    const [staffFormError, setStaffFormError] = useState<string | null>(null);
    const [isSavingStaff, setIsSavingStaff] = useState(false);

    useEffect(() => {
        if (isOpen && group) {
            loadStaffData(group.id);
        }
    }, [isOpen, group]);

    const loadStaffData = async (gId: string) => {
        setIsLoadingStaff(true);
        try {
            const list = await storageService.getStaffByGroup(gId);
            setStaffList(list);
        } catch (e) {
            console.error("Error cargando staff:", e);
        } finally {
            setIsLoadingStaff(false);
        }
    };

    if (!isOpen || !group) return null;

    // --- HANDLERS ---
    const handleStaffSave = async () => {
        setStaffFormError(null);
        if (!staffForm.name.trim()) { setStaffFormError('El nombre es requerido.'); return; }
        if (!staffForm.email.trim()) { setStaffFormError('El correo es requerido para administradores corporativos.'); return; }
        if (!staffForm.email.includes('@')) { setStaffFormError('Ingresa un correo válido.'); return; }

        setIsSavingStaff(true);
        try {
            if (editingStaff) {
                const updates: any = { name: staffForm.name, email: staffForm.email };
                const ok = await storageService.updateStaffUser(editingStaff.id, updates);
                if (!ok) {
                    setStaffFormError('Error al actualizar el usuario. Verifica permisos.');
                    return;
                }
            } else {
                const newData: any = { 
                    name: staffForm.name, 
                    role: 'GROUP_ADMIN' as StaffRole, 
                    email: staffForm.email
                };
                const result = await storageService.createGroupStaff(group.id, newData);
                if (!result.success) {
                    setStaffFormError(result.error || 'Error desconocido al crear el directivo.');
                    return;
                }
            }
            setIsStaffFormOpen(false);
            loadStaffData(group.id);
            setStaffForm({ name: '', email: '' });
        } catch (e: any) {
            setStaffFormError(e?.message || 'Ocurrió un error al guardar el usuario.');
        } finally {
            setIsSavingStaff(false);
        }
    };

    const baseUrl = window.location.origin;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
            <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col my-auto max-h-[90vh]">

                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                            <Building2 className="w-6 h-6 text-indigo-600" />
                            Accesos Corporativos
                        </h2>
                        <p className="text-sm text-slate-500 font-medium mt-1">{group.name}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
                    <div className="animate-fade-in flex flex-col h-full">
                        {!isStaffFormOpen ? (
                            <>
                                <div className="flex justify-between items-center mb-6">
                                    <div>
                                        <h3 className="font-bold text-slate-800 text-lg">Directorio de Corporativo</h3>
                                        <p className="text-sm text-slate-500">Administra los directivos (GROUP_ADMIN) de esta cadena.</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setEditingStaff(null);
                                            setStaffForm({ name: '', email: '' });
                                            setStaffFormError(null);
                                            setIsStaffFormOpen(true);
                                        }}
                                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-5 rounded-xl flex items-center gap-2 shadow-sm transition-all text-sm"
                                    >
                                        <Plus className="w-4 h-4" /> Agregar Directivo
                                    </button>
                                </div>

                                {isLoadingStaff ? (
                                    <div className="flex-1 flex items-center justify-center p-12">
                                        <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                                    </div>
                                ) : staffList.length === 0 ? (
                                    <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center flex-1 flex flex-col items-center justify-center">
                                        <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                        <p className="text-slate-600 font-bold text-lg mb-2">Sin directivos registrados</p>
                                        <p className="text-slate-500 text-sm max-w-sm">
                                            Agrega a los administradores corporativos que supervisarán todos los hospitales de esta cadena.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                                        <table className="w-full">
                                            <thead className="bg-slate-50 border-b border-slate-200 text-left">
                                                <tr>
                                                    <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Nombre</th>
                                                    <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Rol</th>
                                                    <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Estado</th>
                                                    <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Alta</th>
                                                    <th className="py-4 px-6"></th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {staffList.map(member => (
                                                    <tr key={member.id} className={`transition-colors hover:bg-slate-50 ${!member.isActive ? 'opacity-50 grayscale' : ''}`}>
                                                        <td className="py-4 px-6">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-bold text-slate-700">{member.name}</span>
                                                                <span className="text-xs text-slate-500 font-medium">{member.email || 'Sin correo'}</span>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-6">
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-indigo-500/10 text-indigo-600 border-indigo-500/20">
                                                                Dir. Grupo
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-6">
                                                            <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${member.isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${member.isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                                                                {member.isActive ? 'Activo' : 'Inactivo'}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 px-6 text-xs text-slate-400 font-medium">
                                                            {new Date(member.createdAt).toLocaleDateString()}
                                                        </td>
                                                        <td className="py-4 px-6">
                                                            <div className="flex items-center gap-1 justify-end">
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingStaff(member);
                                                                        setStaffForm({ name: member.name, email: member.email || '' });
                                                                        setStaffFormError(null);
                                                                        setIsStaffFormOpen(true);
                                                                    }}
                                                                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                                                    title="Editar"
                                                                >
                                                                    <Edit className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={async () => {
                                                                        await storageService.updateStaffUser(member.id, { isActive: !member.isActive });
                                                                        loadStaffData(group.id);
                                                                    }}
                                                                    className={`p-2 rounded-lg transition-all ${member.isActive ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:bg-slate-100'}`}
                                                                    title={member.isActive ? "Desactivar" : "Activar"}
                                                                >
                                                                    <Power className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={async () => {
                                                                        if (window.confirm(`¿Seguro que deseas eliminar el acceso de ${member.name}?`)) {
                                                                            await storageService.deleteStaffUser(member.id);
                                                                            loadStaffData(group.id);
                                                                        }
                                                                    }}
                                                                    className="p-2 text-red-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                                    title="Eliminar"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        ) : (
                            // --- STAFF FORM ---
                            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm max-w-2xl mx-auto w-full">
                                <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                                    <button onClick={() => setIsStaffFormOpen(false)} className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg transition-colors">
                                        <X className="w-4 h-4" />
                                    </button>
                                    {editingStaff ? 'Editar Acceso Corporativo' : 'Nuevo Acceso Corporativo'}
                                </h3>

                                <div className="space-y-5">
                                    <div>
                                        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block">Nombre Completo</label>
                                        <input
                                            type="text"
                                            value={staffForm.name}
                                            onChange={e => setStaffForm(f => ({ ...f, name: e.target.value }))}
                                            placeholder="Ej: Lic. Mariana Ramos"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-slate-700 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block">Correo Electrónico (Cloudflare Zero Trust)</label>
                                        <div className="animate-fade-in space-y-3">
                                            <input
                                                type="email"
                                                value={staffForm.email}
                                                onChange={e => setStaffForm(f => ({ ...f, email: e.target.value.toLowerCase().trim() }))}
                                                placeholder="ejemplo@hospital-status.com"
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-slate-700 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
                                            />
                                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
                                                <ShieldCheck className="w-5 h-5 text-amber-500 flex-shrink-0" />
                                                <p className="text-xs text-amber-800 leading-relaxed font-medium">
                                                    <strong>Recordatorio:</strong> Este correo debe registrarse o tener permiso en <strong>Cloudflare Zero Trust</strong> para poder pasar el filtro inicial de seguridad. El corporativo se autentica directo en Zero Trust y no usa PIN.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {staffFormError && (
                                        <div className="flex items-center gap-2 bg-red-50 text-red-600 border border-red-100 rounded-xl px-4 py-3 text-sm font-bold">
                                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                            <p>{staffFormError}</p>
                                        </div>
                                    )}

                                    <div className="pt-4 flex gap-3">
                                        <button onClick={() => setIsStaffFormOpen(false)} className="px-6 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold text-sm transition-colors">Cancelar</button>
                                        <button
                                            onClick={handleStaffSave}
                                            disabled={isSavingStaff}
                                            className="flex-1 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {isSavingStaff ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                                            {editingStaff ? 'Guardar Cambios' : 'Crear Directivo'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer contextual */}
                <div className="p-5 border-t border-slate-100 bg-slate-50 text-center flex justify-between items-center">
                    <div className="text-xs text-slate-500 font-medium flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-indigo-500" /> Nivel: GROUP_ADMIN
                    </div>
                    {!isStaffFormOpen && (
                        <button onClick={onClose} className="px-6 py-2 bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 font-bold rounded-xl transition-colors shadow-sm text-sm">
                            Cerrar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GroupAccessModal;
